<template>
  <router-link
    v-if="to"
    :to="to"
    class="bb"
    :class="[`bb--${variant}`, `bb--${size}`, { 'bb--block': block, 'bb--loading': loading }]"
    @click="$emit('click', $event)"
  >
    <span v-if="loading" class="bb__spinner" aria-hidden="true"></span>
    <span class="bb__label"><slot></slot></span>
  </router-link>
  <button
    v-else
    :type="type"
    class="bb"
    :class="[`bb--${variant}`, `bb--${size}`, { 'bb--block': block, 'bb--loading': loading }]"
    :disabled="disabled || loading"
    @click="$emit('click', $event)"
  >
    <span v-if="loading" class="bb__spinner" aria-hidden="true"></span>
    <span class="bb__label"><slot></slot></span>
  </button>
</template>

<script>
export default {
  name: 'BananaButton',
  props: {
    to: { type: [String, Object], default: null },
    type: { type: String, default: 'button' },
    variant: { type: String, default: 'primary' }, // primary | ghost | outline | danger | dark
    size: { type: String, default: 'md' }, // sm | md | lg
    block: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  emits: ['click'],
}
</script>

<style scoped>
.bb {
  --bb-bg: var(--banana-500);
  --bb-fg: var(--ink-900);
  --bb-border: var(--border-c);
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5em;
  font-family: var(--font-display);
  font-weight: 700;
  letter-spacing: -0.01em;
  border: 2.5px solid var(--bb-border);
  background: var(--bb-bg);
  color: var(--bb-fg);
  border-radius: var(--r-pill);
  cursor: pointer;
  transition: transform 0.15s var(--ease-bounce), box-shadow 0.15s var(--ease-out), background 0.2s;
  box-shadow: var(--shadow-pop);
  text-decoration: none;
  white-space: nowrap;
  max-width: 100%;
}
/* Grain matter on solid-fill variants (primary/dark/danger) — same fractal
   noise as the page & footer, so the buttons feel like the same material.
   Ghost/outline opt out (transparent/light fills) via --bb-grain: none. */
.bb::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.10;
  mix-blend-mode: overlay;
  background-image: var(--bb-grain, url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='bn'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23bn)'/%3E%3C/svg%3E"));
}
.bb__label, .bb__spinner { position: relative; z-index: 1; }
.bb:hover:not(:disabled) { transform: translate(-2px, -2px); box-shadow: 6px 6px 0 var(--border-c); }
.bb:active:not(:disabled) { transform: translate(2px, 2px); box-shadow: 1px 1px 0 var(--border-c); }
.bb:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: var(--shadow-pop); }

/* Sizes */
.bb--sm { padding: 7px 16px; font-size: 0.85rem; }
.bb--md { padding: 11px 24px; font-size: 1rem; }
.bb--lg { padding: 15px 34px; font-size: 1.15rem; }
.bb--block { display: flex; width: 100%; }

/* On very small screens, let long labels wrap and tighten large padding
   so a single wide button never forces horizontal overflow. */
@media (max-width: 380px) {
  .bb { white-space: normal; text-align: center; }
  .bb--lg { padding: 14px 22px; font-size: 1.05rem; }
}

/* Variants */
.bb--ghost {
  --bb-bg: transparent;
  --bb-border: transparent;
  --bb-grain: none;
  box-shadow: none;
}
.bb--ghost:hover:not(:disabled) { --bb-bg: var(--banana-100); box-shadow: none; transform: none; }
.bb--ghost:active:not(:disabled) { transform: scale(0.97); box-shadow: none; }

.bb--outline { --bb-bg: var(--cream); --bb-grain: none; }
.bb--danger { --bb-bg: var(--bruise); --bb-fg: #fff; }
.bb--dark { --bb-bg: var(--choco); --bb-fg: var(--banana-400); }

.bb--loading .bb__label { opacity: 0.7; }
.bb__spinner {
  width: 1em; height: 1em;
  border: 2.5px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: bb-spin 0.7s linear infinite;
}
@keyframes bb-spin { to { transform: rotate(360deg); } }

</style>
